import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Compass, Building } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ProjectParametersSection from './ProjectParametersSection';
import DesignOptionsSection from './DesignOptionsSection';
import VisualizationSection from './VisualizationSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости архитектурных решений
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  breakdown,
  setBreakdown,
  projectTypes,
  complexityLevels,
  projectStages,
  visualizationTypes,
  locationTypes,
  urgencyTypes,
  saveProject
}) => {
  
  // Состояние для имени сохраняемого проекта
  const [saveProjectName, setSaveProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Автоматически обновляем поля, связанные с типом проекта
      if (name === 'projectType') {
        const selectedProjectType = projectTypes.find(type => type.id === fieldValue);
        if (selectedProjectType) {
          // Здесь можно добавить логику для автоматического изменения других полей в зависимости от типа проекта
        }
      }

      // Сбрасываем поля визуализации если она отключена
      if (name === 'includeVisualization' && !checked) {
        newState.visualizationType = '3d_renders';
        newState.numVisualizations = '3';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [projectTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      projectType, buildingArea, numFloors, complexity,
      includeVisualization, visualizationType, numVisualizations,
      revisionRounds, urgency, projectStage, location
    } = formData;

    // Парсинг числовых значений
    const numBuildingArea = parseFloatOrDefault(buildingArea, -1);
    const numFloorCount = parseIntOrDefault(numFloors, -1);
    const numVisualizationCount = parseIntOrDefault(numVisualizations, -1);
    const numRevisionRounds = parseIntOrDefault(revisionRounds, -1);

    // Валидация базовых полей
    if (!projectType) newErrors.projectType = 'Выберите тип проекта.';
    
    if (numBuildingArea <= 0) newErrors.buildingArea = 'Площадь должна быть положительной.';
    if (!newErrors.buildingArea && numBuildingArea > 10000) newErrors.buildingArea = 'Максимальная площадь 10000 м².';
    
    if (numFloorCount <= 0) newErrors.numFloors = 'Количество этажей должно быть положительным.';
    if (!newErrors.numFloors && numFloorCount > 100) newErrors.numFloors = 'Максимальное количество этажей 100.';
    
    if (!complexity) newErrors.complexity = 'Выберите уровень сложности.';
    if (!location) newErrors.location = 'Выберите тип местности.';
    if (!projectStage) newErrors.projectStage = 'Выберите этап проектирования.';
    if (!urgency) newErrors.urgency = 'Выберите срочность проекта.';

    // Валидация визуализации
    if (includeVisualization) {
      if (!visualizationType) newErrors.visualizationType = 'Выберите тип визуализации.';
      
      if (numVisualizationCount <= 0) newErrors.numVisualizations = 'Количество визуализаций должно быть положительным.';
      if (!newErrors.numVisualizations && numVisualizationCount > 20) {
        newErrors.numVisualizations = 'Максимальное количество визуализаций 20.';
      }
    }

    // Валидация количества итераций правок
    if (numRevisionRounds <= 0) newErrors.revisionRounds = 'Количество итераций правок должно быть положительным.';
    if (!newErrors.revisionRounds && numRevisionRounds > 10) {
      newErrors.revisionRounds = 'Максимальное количество итераций правок 10.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета стоимости архитектурных решений
  const calculateCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const buildingArea = parseFloatOrDefault(formData.buildingArea);
        const numFloors = parseIntOrDefault(formData.numFloors);
        
        // Получаем базовую ставку за квадратный метр по типу проекта
        const selectedProjectType = projectTypes.find(type => type.id === formData.projectType);
        const baseRatePerSqm = selectedProjectType ? selectedProjectType.defaultRatePerSqm : 1000;
        
        // Получаем множитель сложности
        const selectedComplexity = complexityLevels.find(level => level.id === formData.complexity);
        const complexityFactor = selectedComplexity ? selectedComplexity.factor : 1.0;
        
        // Получаем множитель локации
        const selectedLocation = locationTypes.find(loc => loc.id === formData.location);
        const locationFactor = selectedLocation ? selectedLocation.factor : 1.0;
        
        // Получаем процент стоимости по этапу проектирования
        const selectedStage = projectStages.find(stage => stage.id === formData.projectStage);
        const stagePercentage = selectedStage ? selectedStage.percentage / 100 : 1.0;
        
        // Получаем множитель срочности
        const selectedUrgency = urgencyTypes.find(u => u.id === formData.urgency);
        const urgencyFactor = selectedUrgency ? selectedUrgency.factor : 1.0;
        
        // --- Расчет базовой стоимости ---
        // Базовая стоимость = Площадь * Ставка/м² * Множитель сложности * Множитель локации * Процент этапа * Множитель срочности
        const baseProjectCost = buildingArea * baseRatePerSqm * complexityFactor * locationFactor * stagePercentage * urgencyFactor;
        
        // Увеличиваем стоимость для многоэтажных зданий
        const floorsFactor = 1 + (numFloors > 1 ? (numFloors - 1) * 0.05 : 0); // +5% за каждый этаж свыше 1
        const floorAdjustedCost = baseProjectCost * floorsFactor;
        
        // --- Расчет дополнительных услуг ---
        
        // Стоимость дизайна интерьера (если включено)
        let interiorDesignCost = 0;
        if (formData.includeInteriorDesign) {
          interiorDesignCost = buildingArea * 800; // Примерная ставка за м² для дизайна интерьера
        }
        
        // Стоимость проектирования инженерных систем (если включено)
        let engineeringSystemsCost = 0;
        if (formData.includeEngineeringSystems) {
          engineeringSystemsCost = buildingArea * 400; // Примерная ставка за м² для инженерных систем
        }
        
        // Стоимость строительной документации (если включено)
        let constructionDocumentationCost = 0;
        if (formData.includeConstructionDocumentation) {
          constructionDocumentationCost = buildingArea * 300; // Примерная ставка за м² для документации
        }
        
        // Стоимость авторского надзора (если включено)
        let supervisionCost = 0;
        if (formData.includeProjectSupervision) {
          supervisionCost = floorAdjustedCost * 0.15; // 15% от стоимости проекта
        }
        
        // Стоимость визуализации (если включено)
        let visualizationCost = 0;
        if (formData.includeVisualization) {
          const selectedVisualizationType = visualizationTypes.find(type => type.id === formData.visualizationType);
          const costPerUnit = selectedVisualizationType ? selectedVisualizationType.costPerUnit : 5000;
          const numVisualizations = parseIntOrDefault(formData.numVisualizations, 3);
          visualizationCost = costPerUnit * numVisualizations;
        }
        
        // Итоговая стоимость проекта
        const totalProjectCost = floorAdjustedCost + interiorDesignCost + engineeringSystemsCost + 
                                 constructionDocumentationCost + supervisionCost + visualizationCost;
        
        // --- Создаем подробную разбивку стоимости ---
        const breakdownItems = [
          {
            id: 'base_cost',
            name: 'Базовая стоимость проектирования',
            description: `${buildingArea} м² × ${baseRatePerSqm} ₽/м² × факторы сложности и локации`,
            amount: floorAdjustedCost,
            essential: true
          }
        ];
        
        if (formData.includeInteriorDesign) {
          breakdownItems.push({
            id: 'interior_design',
            name: 'Дизайн интерьера',
            description: `${buildingArea} м² × 800 ₽/м²`,
            amount: interiorDesignCost,
            essential: false
          });
        }
        
        if (formData.includeEngineeringSystems) {
          breakdownItems.push({
            id: 'engineering_systems',
            name: 'Проектирование инженерных систем',
            description: `${buildingArea} м² × 400 ₽/м²`,
            amount: engineeringSystemsCost,
            essential: false
          });
        }
        
        if (formData.includeConstructionDocumentation) {
          breakdownItems.push({
            id: 'construction_docs',
            name: 'Строительная документация',
            description: `${buildingArea} м² × 300 ₽/м²`,
            amount: constructionDocumentationCost,
            essential: false
          });
        }
        
        if (formData.includeProjectSupervision) {
          breakdownItems.push({
            id: 'supervision',
            name: 'Авторский надзор',
            description: '15% от базовой стоимости проекта',
            amount: supervisionCost,
            essential: false
          });
        }
        
        if (formData.includeVisualization) {
          const selectedVisualizationType = visualizationTypes.find(type => type.id === formData.visualizationType);
          const visualizationTypeName = selectedVisualizationType ? selectedVisualizationType.name : 'Визуализация';
          breakdownItems.push({
            id: 'visualization',
            name: `${visualizationTypeName}`,
            description: `${formData.numVisualizations} × ${visualizationTypes.find(type => type.id === formData.visualizationType)?.costPerUnit} ₽`,
            amount: visualizationCost,
            essential: false
          });
        }
        
        // Формируем результаты расчета
        const finalResults = {
          projectType: selectedProjectType ? selectedProjectType.name : formData.projectType,
          buildingArea: buildingArea,
          numFloors: numFloors,
          complexity: selectedComplexity ? selectedComplexity.name : formData.complexity,
          location: selectedLocation ? selectedLocation.name : formData.location,
          projectStage: selectedStage ? selectedStage.name : formData.projectStage,
          
          baseRatePerSqm: baseRatePerSqm,
          baseCost: floorAdjustedCost,
          interiorDesignCost: interiorDesignCost,
          engineeringSystemsCost: engineeringSystemsCost,
          constructionDocumentationCost: constructionDocumentationCost, 
          supervisionCost: supervisionCost,
          visualizationCost: visualizationCost,
          
          totalCost: totalProjectCost,
          currency: formData.currencyType,
          
          // Прогнозируемые сроки реализации (упрощенный расчет)
          timelineWeeks: Math.round(buildingArea / 100 * complexityFactor * 
                        (formData.urgency === 'high' ? 0.7 : formData.urgency === 'very_high' ? 0.5 : 1)),
          
          // Сохраняем разбивку для отображения
          breakdownItems: breakdownItems
        };
        
        setResults(finalResults);
        setBreakdown(breakdownItems);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setBreakdown, setIsCalculating, projectTypes, complexityLevels, locationTypes, projectStages, urgencyTypes, visualizationTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      projectType: 'residential',
      buildingArea: '150',
      numFloors: '2',
      complexity: 'medium',
      location: 'city',
      projectStage: 'concept',
      includeInteriorDesign: false,
      includeEngineeringSystems: true,
      includeConstructionDocumentation: true,
      includeProjectSupervision: false,
      includeVisualization: true,
      visualizationType: '3d_renders',
      numVisualizations: '3',
      revisionRounds: '2',
      urgency: 'normal',
      additionalRequirements: '',
      clientType: 'individual',
      currencyType: 'RUB'
    });
    setResults(null);
    setBreakdown([]);
    setErrors({});
    setSaveProjectName('');
    setShowSaveDialog(false);
  };

  // Экспорт детальной разбивки в CSV
  const exportToCSV = () => {
    if (!breakdown || breakdown.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Наименование', 'Описание', 'Стоимость, ₽'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = breakdown.map(item => {
      return [
        item.name,
        item.description,
        formatCsvNumber(item.amount)
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      'ИТОГО:', 
      '',
      formatCsvNumber(results.totalCost)
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Тип проекта:', results.projectType],
      ['Площадь:', `${results.buildingArea} м²`],
      ['Этажность:', `${results.numFloors}`],
      ['Сложность:', results.complexity],
      ['Этап проектирования:', results.projectStage],
      ['Местоположение:', results.location],
      ['Расчетные сроки:', `${results.timelineWeeks} недель`],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'смета_архитектурного_проекта.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения проекта
  const handleSaveProject = () => {
    if (showSaveDialog) {
      const savedResult = saveProject(saveProjectName || null);
      if (savedResult) {
        alert(`Проект "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProjectName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Building className="w-6 h-6 text-indigo-500" />
          Параметры архитектурного проекта
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры проекта */}
        <ProjectParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          projectTypes={projectTypes}
          complexityLevels={complexityLevels}
          locationTypes={locationTypes}
        />

        {/* Параметры проектирования */}
        <DesignOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          projectStages={projectStages}
          urgencyTypes={urgencyTypes}
        />

        {/* Визуализация */}
        <VisualizationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          visualizationTypes={visualizationTypes}
        />

        {/* Дополнительные опции */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения проекта */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProjectName}
                onChange={(e) => setSaveProjectName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProject}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {breakdown.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать смету (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProject}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;